import merge from 'lodash/merge';
import isObject from 'lodash/isObject';
import moment from 'moment';
import queryStringParser from 'utils/queryStringParser';
import { fromCents, toCents } from 'utils/helpers';
import { getCachedQueryString } from 'utils/localRepository';
import {
  STEP_LOAN_PURPOSE,
  STEP_SYSTEM_AUTHORITY,
  STEP_ALMOST_THERE,
  STEP_INFORMATION,
  STEP_SACC_LOADING,
  STEP_EMAIL_VERIFICATION,
  STEP_LOAN_PURPOSE_AU,
} from 'consts/loan/steps';

let queryString = location.search;
if (COUNTRY === 'FI') {
  queryString = getCachedQueryString() || queryString;
}

const queryStringParams = queryStringParser.parseAndMakeKeysLowerCase(queryString);

const { doubleslider } = queryStringParams;
const doubleSlider = doubleslider && doubleslider.toLowerCase();
const isTotalCostShownOnSlider = ['true', 'full'].indexOf(doubleSlider) !== -1;

let maturity = parseInt(queryStringParams.maturity, 10);
let principal = toCents(queryStringParams.principal);
if (COUNTRY === 'PL' && sessionStorage) {
  const savedProduct = sessionStorage.getItem('productSelected');
  if (savedProduct) {
    const parsedProduct = JSON.parse(savedProduct);
    if (isObject(parsedProduct)) {
      if (!maturity) {
        maturity = parsedProduct.maturity;
      }
      if (!principal) {
        principal = parsedProduct.principal;
      }
    }
  }
}
if (COUNTRY === 'LT' && sessionStorage) {
  if (!sessionStorage.getItem('offerSelected')) {
    sessionStorage.setItem('offerSelected', principal ? fromCents(principal) : 0);
  }
}

let robinRedirectId = null;
if (COUNTRY === 'ES') {
  if (queryStringParams.id
    && queryStringParams.source
    && queryStringParams.source.indexOf('RobinAcademia') !== -1) {
    robinRedirectId = queryStringParams.id;
  }
}

const baseCustomerState = {
  email: '',
  bankAccount: '',
  communicationSettings: {},
  address: {},
};

const baseInitialState = {
  mmps: null,
  mmpsByMonth: null,
  mmpsByGroup: {},
  isServiceUnavailable: false,
  isLoanSubmitted: false,
  isLoanSubmitting: false,
  isContentLoading: false,
  isRegisterDataLoading: false,
  isLoadingMMP: false,
  isApplicationWithProduct: false,
  sliderConfig: {
    isVisible: queryStringParams.homeslider !== 'false',
    showsMaturityScale: ['true', 'full', 'simple'].indexOf(doubleSlider) !== -1,
    showsTotalLoanAmount: isTotalCostShownOnSlider,
  },
  currentStep: 0,
  customer: {},
  product: {
    maxMaturity: 36,
    minMaturity: 3,
    maturityStep: 1,
    frontPagePrincipal: principal,
  },
  canPutFinancialData: false,
  state: 'OPEN',
  rejected: null,
  selectedProduct: {},
  authenticationPostOfficeId: null,
  postOffices: [],
  loanIssuers: [],
  loanDueDateRange: {},
  productType: 'CREDIT_LINE',
  isLoanFormFooterVisible: true,
  formInitalValues: {},
  isFilledByBroker: false,
  isFilledByBrokerPayLater: false,
  brokerUrl: null,
  canChangeLimit: false,
};

const eeState = {
  steps: [
    { type: 'LOGIN', path: 'loan' },
    { type: 'CUSTOMER', path: 'loan/customer', analyticsStepName: 1 },
    { type: 'ADDRESS', path: 'loan/address', analyticsStepName: 1.1 },
    { type: 'INCOME', path: 'loan/income', analyticsStepName: 2 },
    { type: 'EXPENSES', path: 'loan/expenses', analyticsStepName: 2.1 },
    { type: 'LOANS', path: 'loan/loans', analyticsStepName: 3 },
    { type: 'MARITAL_STATUS', path: 'loan/marital-status', analyticsStepName: 3.1 },
    { type: 'HOUSEHOLD_CHILDREN', path: 'loan/household-children', analyticsStepName: 4 },
    { type: 'EDUCATION', path: 'loan/education', analyticsStepName: 4.1 },
    { type: 'WORK_STATUS', path: 'loan/work-status', analyticsStepName: 5 },
    { type: 'RESIDENCE', path: 'loan/residence', analyticsStepName: 6 },
    STEP_SYSTEM_AUTHORITY,
    STEP_LOAN_PURPOSE,
    { type: 'PRODUCT', path: 'loan/offer', hideFromProgressBar: true },
    { type: 'CREDIT_LINE', path: 'loan/offer/credit-line', hideFromProgressBar: true },
    { type: 'NORDIGEN', path: 'loan/offer/nordigen', hideFromProgressBar: true },
    { type: 'CONTRACT_DETAILS', path: 'loan/offer/bank-account', hideFromProgressBar: true },
    { type: 'SUBMIT', path: 'loan/offer/submit', hideFromProgressBar: true },
  ],
  customer: baseCustomerState,
  product: {
    maxMaturity: 60,
    maturityPeriods: isTotalCostShownOnSlider ? 6 : 60,
    principal: 100000,
    step: 5000,
    minPrincipal: 5000,
    maxPrincipal: 500000,
  },
  financialData: {
    debtTypesExperienced: [],
    education: null,
    employmentDuration: null,
    maritalStatus: null,
    livingCosts: null,
    netIncome: null,
    company: null,
    occupation: null,
    occupationType: null,
    activityType: null,
    otherDebts: [],
    residence: null,
    householdChildren: null,
  },
  loanPurpose: null,
  chooseNordigenConfirm: false,
  showNordigenError: false,
  channelList: [],
  accountFromNordigen: [],
  skipNordigenConfirm: false,
};

const lvSharedSteps = [
  { type: 'LOGIN', path: 'loan' },
  { type: 'CUSTOMER_LV', path: 'loan/customer', analyticsStepName: 1 },
  { type: 'ADDRESS', path: 'loan/address', analyticsStepName: 2 },
  { type: 'INCOME', path: 'loan/income', analyticsStepName: 3 },
  { type: 'LOAN_EXPENSES', path: 'loan/loan-expenses', analyticsStepName: 3.1 },
  { type: 'MARITAL_STATUS', path: 'loan/marital-status', analyticsStepName: 4 },
  { type: 'EDUCATION', path: 'loan/education', analyticsStepName: 4.1 },
  { type: 'WORK_STATUS', path: 'loan/work-status', analyticsStepName: 5 },
  { type: 'RESIDENCE', path: 'loan/residence', analyticsStepName: 6 },
  { type: 'LOAN_PURPOSE', path: 'loan/loan-purpose' },
  { type: 'PRODUCT', path: 'loan/offer', hideFromProgressBar: true },
  { type: 'SUBMIT', path: 'loan/offer/submit', hideFromProgressBar: true },
];

const lvState = {
  steps: [
    ...lvSharedSteps,
    {
      type: 'ONE_CENT',
      path: 'loan/offer/one-cent',
      analyticsStepName: 'verificationOneCent',
      hideFromProgressBar: true,
    },
  ],
  product: {
    maturityPeriods: isTotalCostShownOnSlider ? 6 : 60,
    maxMaturity: 60,
    principal: 100000,
    step: 5000,
    minPrincipal: 10000,
    maxPrincipal: 500000,
    initialPrincipalStep: 50,
    principalBreakpoints: {
      700: 100,
      2000: 500,
    },
  },
  customer: baseCustomerState,
  availableProducts: [],
  financialData: {
    education: null,
    employmentDuration: null,
    maritalStatus: null,
    livingCosts: 0,
    netIncome: null,
    company: null,
    occupation: null,
    occupationType: null,
    residence: null,
    householdChildren: 0,
    totalMonthlyObligations: null,
    loans: [],
    loanPurpose: 'OTHER',
  },
  paymentPlan: null,
  applicableCampaigns: [],
  technicalError: false,
};

const lvInstState = {
  steps: [
    ...lvSharedSteps,
    {
      type: 'VERIFY', path: 'loan/offer/verify', analyticsStepName: 'purchase', hideFromProgressBar: true,
    },
    { type: 'INSTANTOR', path: 'loan/offer/instantor', hideFromProgressBar: true },
    {
      type: 'ONE_CENT',
      path: 'loan/offer/one-cent',
      analyticsStepName: 'verificationOneCent',
      hideFromProgressBar: true,
    },
  ],
  product: {
    maturityPeriods: isTotalCostShownOnSlider ? 6 : 60,
    maxMaturity: 60,
    minMaturity: 1,
    principal: 100000,
    step: 5000,
    minPrincipal: 10000,
    maxPrincipal: 500000,
    initialPrincipalStep: 50,
    principalBreakpoints: {
      700: 100,
      2000: 500,
    },
  },
  customer: baseCustomerState,
  availableProducts: [],
  financialData: {
    education: null,
    employmentDuration: null,
    maritalStatus: null,
    livingCosts: 0,
    netIncome: null,
    company: null,
    occupation: null,
    occupationType: null,
    residence: null,
    householdChildren: 0,
    totalMonthlyObligations: null,
    loans: [],
    loanPurpose: 'OTHER',
  },
  documentRequirements: [],
  formInitalValues: {
    verifyOptions: 'instantor',
  },
  paymentPlan: null,
  applicableCampaigns: [],
  instantor: {
    status: null,
  },
};

const fiSharedState = {
  product: {
    maturityPeriods: isTotalCostShownOnSlider ? 12 : 36,
    principal: 50000,
    step: 5000,
    minPrincipal: 5000,
    maxPrincipal: 400000,
  },
  customer: baseCustomerState,
  availableProducts: [],
  financialData: {
    benefits: 0,
    education: null,
    employmentDuration: null,
    housingCosts: 0,
    livingCosts: 0,
    loans: [],
    occupation: null,
    occupationType: null,
    otherIncome: 0,
    otherLoans: 0,
    pension: 0,
    residence: null,
    salary: 0,
  },
  paymentPlan: null,
  applicableCampaigns: [],
};

const fiState = {
  ...fiSharedState,
  financialData: {
    ...fiSharedState.financialData,
  },
  steps: [
    { type: 'LOGIN', path: 'loan', hideFromProgressBar: true },
    { type: 'CUSTOMER_FI', path: 'loan/customer', analyticsStepName: 1 },
    { type: 'INCOME_FI', path: 'loan/income', analyticsStepName: 2 },
    { type: 'LOANS_FI', path: 'loan/loans' },
    { type: 'EDUCATION', path: 'loan/education', analyticsStepName: 3 },
    { type: 'WORK_STATUS', path: 'loan/work-status', analyticsStepName: 4 },
    { type: 'RESIDENCE', path: 'loan/residence', analyticsStepName: 5 },
    { type: 'BANK_ACCOUNT', path: 'loan/bank-account', analyticsStepName: 6 },
    { type: 'HOUSEHOLD_CHILDREN', path: 'loan/household-children', analyticsStepName: 6.1 },
    { type: 'PRODUCT', path: 'loan/offer', hideFromProgressBar: true },
    {
      type: 'PAYMENT_DAY', path: 'loan/offer/payment-day', analyticsStepName: 8, hideFromProgressBar: true,
    },
    { type: 'SUBMIT', path: 'loan/offer/submit', hideFromProgressBar: true },
    { type: 'NORDIGEN_INFO', path: 'loan/offer/nordigen-info', hideFromProgressBar: true },
    { type: 'NORDIGEN_WIDGET', path: 'loan/offer/nordigen-widget', hideFromProgressBar: true },
  ],
};

if (!VARIANT_FI_ZERO_DRAW) {
  fiState.financialData.householdChildren = 0;
}

const fiMWState = {
  ...fiSharedState,
  steps: [
    { type: 'LOGIN', path: 'loan' },
    { type: 'CUSTOMER_FI', path: 'loan/customer', analyticsStepName: 1 },
    { type: 'INCOME_FI', path: 'loan/income', analyticsStepName: 2 },
    { type: 'LOANS_FI', path: 'loan/loans' },
    { type: 'EDUCATION', path: 'loan/education', analyticsStepName: 3 },
    { type: 'WORK_STATUS', path: 'loan/work-status', analyticsStepName: 4 },
    { type: 'RESIDENCE', path: 'loan/residence', analyticsStepName: 5 },
    { type: 'PRODUCT', path: 'loan/offer', hideFromProgressBar: true },
    { type: 'SUBMIT', path: 'loan/offer/submit', hideFromProgressBar: true },
  ],
};

const mxCustomer = baseCustomerState;

if (/^PAYROLL/.test(queryStringParams.channel) && queryStringParams.company) {
  mxCustomer.company = queryStringParams.company;
}

const mxState = {
  steps: [
    {
      type: 'LOGIN',
      path: 'loan',
    },
    {
      type: 'CUSTOMER_MX_NAMES',
      path: 'loan/customer',
    },
    {
      type: 'CUSTOMER_MX_CONTACTS',
      path: 'loan/customer-contacts',
      analyticsStepName: '1',
    },
    {
      type: 'CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION',
      path: 'loan/bank-account-verification',
      analyticsStepName: '1.1',
    },
    {
      type: 'CUSTOMER_MX_ADDRESS',
      path: 'loan/customer-address',
      analyticsStepName: '2',
    },
    {
      type: 'FAMILY_MX',
      path: 'loan/family',
      analyticsStepName: '3',
    },
    {
      type: 'EDUCATION_WORK_MX',
      path: 'loan/education-work',
      analyticsStepName: '4',
    },
    {
      type: 'INCOME_MX',
      path: 'loan/income',
      analyticsStepName: '5',
    },
    {
      type: 'PRODUCT',
      path: 'loan/offer',
      showInProgressBar: true,
    },
    {
      type: 'CONFIRM_ZERO_DRAW',
      path: 'loan/offer/confirm',
      showProgressBar: true,
    },
    {
      type: 'BANK_ACCOUNT',
      path: 'loan/offer/bank-account',
      showInProgressBar: true,
      analyticsStepName: '7',
    },
    {
      type: 'SUBMIT',
      path: 'loan/offer/submit',
      showInProgressBar: true,
      analyticsStepName: '8',
    },
    {
      type: 'REFERENCE',
      path: 'loan/offer/reference',
      showInProgressBar: true,
    },
    {
      type: 'ONFIDO_VERIFICATION',
      path: 'loan/offer/onfido-verification',
      showInProgressBar: true,
      analyticsStepName: 'verificationOnfido',
    },
    {
      type: 'VERIFICATION',
      path: 'loan/offer/verification',
      showInProgressBar: true,
      analyticsStepName: 'verificationJUMIO',
    },
  ],
  product: {
    principal: 1500000,
    step: 100000,
    minPrincipal: 500000,
    maxPrincipal: 4000000,
  },
  customer: mxCustomer,
  financialData: {
    education: null,
    company: null,
    occupation: null,
    netIncome: null,
    incomeFrequency: null,
    residence: null,
    maritalStatus: null,
    loans: [],
    creditBureauQuestion1Answer: false,
  },
  mxAddresses: [],
  areAddressDetailsHidden: true,
  mxBanks: [],
  emailInUse: '',
  formInitalValues: {},
  onfidoSucceeded: null,
};

const esSteps = [
  { type: 'LOGIN', path: 'loan' },
  {
    type: 'FAST_TRACK',
    path: 'loan/fast-track',
    navTitle: 'navigation.step.FAST_TRACK',
  },
  {
    type: 'CUSTOMER_ES_NAMES',
    path: 'loan/customer',
    showInProgressBar: true,
    navTitle: 'navigation.step.CUSTOMER_ES_NAMES',
    analyticsStepName: '1',
    fields: ['firstName', 'secondFirstName', 'lastName', 'secondLastName'],
  },
  {
    type: 'CUSTOMER_BIRTH_DETAILS',
    path: 'loan/customer-birth',
    showInProgressBar: true,
    navTitle: 'navigation.step.CUSTOMER_BIRTH_DETAILS',
    analyticsStepName: '2',
    fields: ['birthDay', 'birthMonth', 'birthYear', 'countryOfBirth'],
  },
  {
    type: 'CUSTOMER_ES_ADDRESS',
    path: 'loan/customer-address',
    showInProgressBar: true,
    navTitle: 'navigation.step.CUSTOMER_ES_ADDRESS',
    analyticsStepName: '3',
    fields: ['address', 'address.street', 'address.streetNumber', 'address.floor', 'address.door', 'address.postcode', 'address.city', 'address.province', 'msisdn2'],
  },
  {
    type: 'CUSTOMER_ES_CONTACTS',
    path: 'loan/customer-contacts',
    showInProgressBar: true,
    navTitle: 'navigation.step.CUSTOMER_ES_CONTACTS',
    analyticsStepName: '4',
    fields: ['email', 'password', 'submitFinancialdataSign'],
  },
  {
    type: 'INCOME',
    path: 'loan/income',
    showInProgressBar: true,
    navTitle: 'navigation.step.INCOME',
    analyticsStepName: '4.1',
    fields: ['netIncome'],
  },
  {
    type: 'LOAN_QUESTIONS',
    path: 'loan/loan-questions',
    showInProgressBar: true,
    navTitle: 'navigation.step.LOAN_QUESTIONS',
    analyticsStepName: '4.2',
    fields: ['loanPurpose'],
  },
  {
    type: 'SCORING_STATUS',
    path: 'loan/status',
  },
  {
    type: 'EDUCATION_WORK_CREDITEA',
    path: 'loan/education-work',
    showInProgressBar: true,
    navTitle: 'navigation.step.EDUCATION_WORK_CREDITEA',
    analyticsStepName: '5',
    fields: ['education', 'occupation', 'employmentDuration'],
  },
  {
    type: 'FAMILY_CREDITEA',
    path: 'loan/family',
    showInProgressBar: true,
    navTitle: 'navigation.step.FAMILY_CREDITEA',
    analyticsStepName: '6',
    click2CallAvailable: true,
    fields: ['maritalStatus'],
  },
  {
    type: 'SCORING_STATUS',
    path: 'loan/status',
  },
  {
    type: 'GUARANTOR',
    path: 'loan/guarantor',
    navTitle: 'navigation.step.GUARANTOR',
    analyticsStepName: '7',
  },
  {
    type: 'PRODUCT',
    path: 'loan/offer',
    click2CallAvailable: true,
  },
  {
    type: 'SUBMIT',
    path: 'loan/offer/submit',
    click2CallAvailable: true,
  },
  {
    type: 'CALMA',
    path: 'loan/offer/calma',
  },
  {
    type: 'PAYMENT_DAY',
    path: 'loan/offer/duedate',
    analyticsStepName: 'EC-duedate',
  },
  {
    type: 'LIBERTO',
    path: 'loan/offer/liberto',
    analyticsStepName: 'EC-cardDetails',
  },
];

const esState = {
  steps: esSteps,
  product: {
    maturityPeriods: 36,
    principal: 100000,
    step: 5000,
    minPrincipal: 10000,
    maxPrincipal: 300000,
    interestValue: 0,
  },
  customer: baseCustomerState,
  rejectedMessage: null,
  financialData: {
    appliedForAmount: null,
    education: null,
    occupation: null,
    netIncome: null,
    residence: null,
    maritalStatus: null,
    householdChildren: null,
    guarantorType: null,
  },
  countries: [],
  robinRedirectId,
  areAddressDetailsHidden: true,
  libertoSucceeded: location.hash.includes('capture-success'),
  libertoUnsucceed: location.hash.includes('!capture-fail'),
  libertoFail: location.hash.includes('!recurrent-fail'),
  libertoSaved: false,
  otpSectionVisible: false,
  pinValidationRequired: false,
  isPinValidationLoading: false,
  eidSucceeded: null,
  statusCheckAttepts: 0,
  statusMaxTimeout: 240000,
  waitingForOfferInterval: 5000,
  eidPopupAttepmt: false,
  fees: null,
  rescoringToDoForOpenCreditApplication: 0,
  lastStatusPage: null,
  contract: null,
};

const esStateWithVerificationSteps = {
  steps: [
    ...esSteps,
    { type: 'VERIFICATION_WAY', path: 'loan/offer/verification-way', analyticsStepName: 'EC-verificationWay' },
    { type: 'KONTOMATIK', path: 'loan/offer/kontomatik', analyticsStepName: 'EC-kontomatik' },
    { type: 'EID_INTRO', path: 'loan/offer/eid-intro', analyticsStepName: 'EC-verificationID' },
    { type: 'EID', path: 'loan/offer/eid', analyticsStepName: 'EC-verificationID' },
  ],
};

const esPartnerPortalState = {
  steps: [
    { type: 'LOGIN', path: 'loan' },
    {
      type: 'CUSTOMER_ES_NAMES',
      path: 'loan/customer',
      showInProgressBar: true,
      navTitle: 'navigation.step.CUSTOMER_ES_NAMES',
      analyticsStepName: '1',
      fields: ['firstName', 'secondFirstName', 'lastName', 'secondLastName'],
    },
    {
      type: 'CUSTOMER_BIRTH_DETAILS',
      path: 'loan/customer-birth',
      showInProgressBar: true,
      navTitle: 'navigation.step.CUSTOMER_BIRTH_DETAILS',
      analyticsStepName: '2',
      fields: ['birthDay', 'birthMonth', 'birthYear', 'countryOfBirth'],
    },
    {
      type: 'CUSTOMER_ES_ADDRESS',
      path: 'loan/customer-address',
      showInProgressBar: true,
      navTitle: 'navigation.step.CUSTOMER_ES_ADDRESS',
      analyticsStepName: '3',
      fields: ['address', 'address.street', 'address.streetNumber', 'address.floor', 'address.door', 'address.postcode', 'address.city', 'address.province', 'msisdn2'],
    },
    {
      type: 'CUSTOMER_ES_CONTACTS',
      path: 'loan/customer-contacts',
      showInProgressBar: true,
      navTitle: 'navigation.step.CUSTOMER_ES_CONTACTS',
      analyticsStepName: '4',
      fields: ['email', 'password', 'submitFinancialdataSign'],
    },
    {
      type: 'INCOME',
      path: 'loan/income',
      showInProgressBar: true,
      navTitle: 'navigation.step.INCOME',
      analyticsStepName: '4.1',
      fields: ['netIncome'],
    },
    {
      type: 'LOAN_QUESTIONS',
      path: 'loan/loan-questions',
      showInProgressBar: true,
      navTitle: 'navigation.step.LOAN_QUESTIONS',
      analyticsStepName: '4.2',
      fields: ['loanPurpose'],
    },
    {
      type: 'SCORING_STATUS',
      path: 'loan/status',
    },
    {
      type: 'EDUCATION_WORK_CREDITEA',
      path: 'loan/education-work',
      showInProgressBar: true,
      navTitle: 'navigation.step.EDUCATION_WORK_CREDITEA',
      analyticsStepName: '5',
      fields: ['education', 'occupation', 'employmentDuration'],
    },
    {
      type: 'FAMILY_CREDITEA',
      path: 'loan/family',
      showInProgressBar: true,
      navTitle: 'navigation.step.FAMILY_CREDITEA',
      analyticsStepName: '6',
      click2CallAvailable: true,
      fields: ['maritalStatus'],
    },
    {
      type: 'SCORING_STATUS',
      path: 'loan/status',
    },
    {
      type: 'GUARANTOR',
      path: 'loan/guarantor',
      navTitle: 'navigation.step.GUARANTOR',
      analyticsStepName: '7',
    },
    {
      type: 'PRODUCT',
      path: 'loan/offer',
      click2CallAvailable: true,
    },
    {
      type: 'SUBMIT',
      path: 'loan/offer/submit',
      click2CallAvailable: true,
    },
    {
      type: 'CALMA',
      path: 'loan/offer/calma',
    },
    {
      type: 'PAYMENT_DAY',
      path: 'loan/offer/duedate',
      analyticsStepName: 'EC-duedate',
    },
    {
      type: 'LIBERTO',
      path: 'loan/offer/liberto',
      analyticsStepName: 'EC-cardDetails',
    },
  ],
  channelList: [],
};

const czState = {
  steps: [
    { type: 'LOGIN', path: 'loan' },
    {
      type: 'CUSTOMER_DETAILS',
      path: 'loan/customer-details',
      showInProgressBar: true,
      analyticsStepName: 'customerDetailsCZ',
    },
    {
      type: 'ADDRESS_CZ',
      path: 'loan/address',
      showInProgressBar: true,
      analyticsStepName: 'addressCZ',
    },
    {
      type: 'OBLIGATIONS',
      path: 'loan/obligations',
      showInProgressBar: true,
      analyticsStepName: 'obligationsCZ',
    },
    {
      type: 'PRODUCT',
      path: 'loan/offer',
    },
    {
      type: 'SUBMIT',
      path: 'loan/offer/submit',
      analyticsStepName: 'legalCZ',
    },
    {
      type: 'CREDIT_LIMIT',
      path: 'loan/offer/credit-limit',
      analyticsStepName: 'creditLimit',
    },
    {
      type: 'INCOME_VERIFICATION_WAY',
      path: 'loan/offer/income-verification',
      analyticsStepName: 'incomeVerificationCZ',
    },
    {
      type: 'IDENTITY_VERIFICATION_WAY',
      path: 'loan/offer/identity-verification',
      analyticsStepName: 'identityVerificationCZ',
    },
  ],
  product: {
    maturityPeriods: 36,
    principal: 100000,
    step: 5000,
    minPrincipal: 10000,
    maxPrincipal: 300000,
  },
  financialData: {},
  incomeVerificationWay: null,
  identityVerificationWay: null,
  pinValidationRequired: false,
  formInitalValues: {
    citizenship: 'CZ',
  },
  addressSuggestions: {
    name: '',
    data: [],
  },
};

const pathPrefix = UNIFIED_APP ? '' : 'loan/';

const plSteps = [
  {
    type: 'CUSTOMER_PL_AGREEMENTS',
    path: `${pathPrefix}agreements`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.CUSTOMER_PL_AGREEMENTS',
    analyticsStepName: 1,
  },
  {
    type: 'ADDRESS',
    path: `${pathPrefix}address`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.ADDRESS',
    analyticsStepName: 2,
  },
  {
    type: 'MARITAL_STATUS_PL',
    path: `${pathPrefix}marital-status`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.MARITAL_STATUS_PL',
    analyticsStepName: 3,
  },
  {
    type: 'EDUCATION_WORK_CREDITEA',
    path: `${pathPrefix}employment`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.EDUCATION_WORK_CREDITEA',
    analyticsStepName: 4,
  },
  {
    type: 'PL_INCOME',
    path: `${pathPrefix}income`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.PL_INCOME',
    analyticsStepName: 5,
  },
  {
    type: 'BANK_ACCOUNT_PL',
    path: `${pathPrefix}bank-account`,
    isLoanStep: true,
    showInProgressBar: true,
    navTitle: 'navigation.step.BANK_ACCOUNT_PL',
    analyticsStepName: 6,
  },
  {
    type: 'PRODUCT',
    path: `${pathPrefix}offer`,
    isLoanStep: true,
  },
  {
    type: 'VAS_CONFIRM',
    path: `${pathPrefix}offer/vas-confirm`,
    isLoanStep: true,
    analyticsStepName: 7.1,
  },
  {
    type: 'CREDENTIALS',
    path: `${pathPrefix}offer/credentials`,
    isLoanStep: true,
  },
  {
    type: 'SUBMIT',
    path: `${pathPrefix}offer/submit`,
    isLoanStep: true,
    analyticsStepName: 8,
  },
  {
    type: 'PL_VERIFY',
    path: `${pathPrefix}offer/verify`,
    isLoanStep: true,
  },
];

const plState = {
  sliderConfig: {
    isVisible: false,
  },
  steps: plSteps,
  product: {
    principal: 450000,
    maturityPeriods: 24,
    minPrincipal: 80000,
    maxPrincipal: 1500000,
    step: 10000,
  },
  customer: baseCustomerState,
  rejectedMessage: null,
  financialData: {},
  formInitalValues: {
    postAddressAsResidence: true,
    employmentProvisionTermination: moment().add(1, 'months').format('YYYY-MM-DD'),
    powerOfAttorney: true,
    creditReliabilityVerification: true,
    termsOfServiceViaWebsite: true,
    firstCitizenship: 'POL',
    countryOfBirth: 'POL',
  },
  addressSuggestions: null,
  communicationSettings: {},
  productType: 'INSTALLMENT',
  citizenships: [],
  isWaitingForOffer: false,
};

const plStateWithLoginStep = {
  steps: [
    { type: 'LOGIN', path: 'login' },
    ...plSteps,
  ],
};

const plStateWithInitStep = {
  steps: [
    { type: 'INIT', path: 'init' },
    ...plSteps,
  ],
};

const auState = {
  sliderConfig: {
    isVisible: false,
  },
  customer: {
    address: {},
    governmentBenefits: false,
  },
  steps: [
    { type: 'LOGIN', path: 'login', hideFromProgressBar: true },
    { type: 'IMPERSONATION_AU', path: 'loan/impersonation', analyticsStepName: 'impersonation' },
    { type: 'CREDIT_SENSE', path: 'loan/bank-details', analyticsStepName: 'bank_statement' },
    { type: 'ADDRESS_AU', path: 'loan/address', analyticsStepName: 'address' },
    { type: 'IDENT_DOCUMENTS', path: 'loan/identification', analyticsStepName: 'identification' },
    { type: 'FAMILY_AU', path: 'loan/family', analyticsStepName: 'family' },
    { type: 'EDUCATION_WORK', path: 'loan/education-work', analyticsStepName: 'employment' },
    { type: 'INCOME_AU', path: 'loan/income', analyticsStepName: 'income' },
    { type: 'EXPENSES_AU', path: 'loan/expenses', analyticsStepName: 'expenses' },
    { type: 'LOANS_AU', path: 'loan/loans', analyticsStepName: 'financial_commitments' },
    {
      type: 'SCORING_STATUS_AU', path: 'loan/status', hideFromProgressBar: true, analyticsStepName: 'scoring',
    },
    { type: 'PRODUCT', path: 'loan/offer', hideFromProgressBar: true },
    STEP_LOAN_PURPOSE_AU,
    STEP_ALMOST_THERE,
    STEP_SACC_LOADING,
    ...(!INCREASE_APP ? [STEP_EMAIL_VERIFICATION] : []),
    {
      type: 'SUBMIT',
      path: 'loan/offer/submit',
      hideFromProgressBar: true,
      analyticsStepName: 'legal',
    },
    STEP_INFORMATION,
    {
      type: 'CONSENTS_AU', path: 'loan/consents', analyticsStepName: 'consents', hideFromProgressBar: true,
    },
  ],
  product: {
    maturityPeriods: 36,
    principal: 300000,
    step: 10000,
    minPrincipal: 50000,
    maxPrincipal: 650000,
    isSACC: false,
  },
  productType: PRODUCT_IL ? 'INSTALLMENT' : 'CREDIT_LINE',
  legalDocument: null,
  loanPurposes: null,
  rejectedMessage: null,
  financialData: {},
  instantMessenger: 'yes',
  areAddressDetailsHidden: true,
  isWaitingForOffer: false,
  waitingForOfferInterval: 5000,
  waitingForOfferCounter: 0,
  waitingForOfferCounterMax: 47,
  bankScrapingTimedOut: false,
  technicalError: false,
  bankScrapingResult: {
    status: queryStringParams.status,
    error: queryStringParams.errortext,
    errorCode: queryStringParams.errorcode,
    referrerCode: queryStringParams.referrercode,
  },
  legacyBalance: null,
  followUpQuestions: [],
  followUpQuestionsLoaded: false,
  followUpQuestionsFetchFail: false,
  shouldShowSACCPage: false,
  countries: [],
};

const getInitialLoanState = () => {
  let initialState;
  switch (COUNTRY) {
    case 'LV':
      if (INSTANTOR_ENABLED) {
        initialState = merge({}, baseInitialState, lvInstState);
        break;
      }
      initialState = merge({}, baseInitialState, lvState);
      break;
    case 'FI':
      initialState = merge({}, baseInitialState, fiState);
      if (MOBILE_WALLET) {
        initialState = merge({}, baseInitialState, fiMWState);
      }
      break;
    case 'MX':
      initialState = merge({}, baseInitialState, mxState);
      break;
    case 'CZ':
      initialState = merge({}, baseInitialState, czState);
      break;
    case 'ES':
      initialState = merge({}, baseInitialState, esState);
      if (PARTNER_PORTAL) {
        initialState = {
          ...initialState,
          ...esPartnerPortalState,
        };
      } else if (!EMBEDDED) {
        initialState = {
          ...initialState,
          ...esStateWithVerificationSteps,
        };
      }
      break;
    case 'PL':
      initialState = merge({}, baseInitialState, plState);
      if (UNIFIED_APP) {
        initialState = {
          ...initialState,
          ...plStateWithInitStep,
        };
      } else {
        initialState = {
          ...initialState,
          ...plStateWithLoginStep,
        };
      }
      break;
    case 'AU':
      initialState = merge({}, baseInitialState, auState);
      break;
    case 'EE':
    default:
      initialState = merge({}, baseInitialState, eeState);
  }
  if (principal) {
    const { product } = initialState;
    const { step, minPrincipal, maxPrincipal } = product;
    principal = (Math.round(principal / step)) * step;
    if (principal >= minPrincipal && principal <= maxPrincipal) {
      initialState.product.principal = principal;
    }
  }
  if (maturity && COUNTRY !== 'MX') {
    const { product } = initialState;
    const { maturityStep, minMaturity, maxMaturity } = product;
    maturity = (Math.round(maturity / maturityStep)) * maturityStep;
    if (maturity >= minMaturity && maturity <= maxMaturity) {
      initialState.product.maturityPeriods = maturity;
    }
  }

  return initialState;
};

export default getInitialLoanState();
